import { api } from '@/lib/api';
import { IAM_BASE } from '@/api/paths';

/**
 * KENDI PROFILI — okuma, guncelleme, hesap silme.
 *
 * `GET /v1/users` bir LISTE UCU DEGIL: sunucu `userId` parametresini yok sayip
 * cagiranin KENDI satirini doner (iam-service.js getUsers, "ownership clamp").
 * Bu yuzden burada `fetchSelf` adiyla duruyor; "kullanici listesi" gibi bir
 * isim, olmayan bir yetenek varmis izlenimi verirdi.
 *
 * `src/api/iam.ts` icindeki `getSelf` ile ayni ucu okuyor ama DAHA GENIS bir
 * tip doner: oturum ozetinin ihtiyaci olmayan `userIdentityId` bu formda
 * duzenlenebilir bir alan. Ozet tipini genisletmek yerine feature kendi
 * gorunumunu tanimliyor.
 */

type Envelope = { code?: string; message?: string };

const ensureSuccess = <T extends Envelope>(data: T): T => {
  // HTTP 200 + code:'error' MUMKUN; yalniz duruma bakan bir cagri sessizce
  // basarili sanir.
  if (data.code !== 'success') throw new Error(data.message ?? 'UNKNOWN_ERROR');
  return data;
};

const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * Formda duzenlenebilen alanlar. IS YERI ALANLARI (isletme adi, vergi dairesi,
 * vergi no) BURADA YOK: karsiliklari olan app_user.user_business_* kolonlari
 * kaldirildi, is yeri bilgisinin tek yeri `public.organization` — yani
 * "Isletmem" ekrani (bkz. features/organization).
 *
 * `labelKey` metin DEGIL ceviri anahtari; dizi modul seviyesinde, t() burada
 * cagrilamaz.
 */
export const PROFILE_FIELDS = [
  { key: 'userFullName', labelKey: 'fields.userFullName' },
  { key: 'userEmail', labelKey: 'fields.userEmail' },
  { key: 'userCity', labelKey: 'fields.userCity' },
  { key: 'userDistrict', labelKey: 'fields.userDistrict' },
  { key: 'userPhone', labelKey: 'fields.userPhone' },
  { key: 'userIdentityId', labelKey: 'fields.userIdentityId' },
] as const;

export type ProfileFieldKey = (typeof PROFILE_FIELDS)[number]['key'];

export type SelfProfile = {
  /** pg int8 -> STRING. Kimlik hicbir yerde sayiya cevrilmez. */
  id: string;
  fields: Record<ProfileFieldKey, string>;
};

type RawUser = Record<string, unknown> & { id?: unknown };

/** pg int8 STRING doner; number yalniz kucuk kimliklerde gorulur. */
const asId = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  return '';
};

const toSelfProfile = (raw: RawUser): SelfProfile => {
  const fields = {} as Record<ProfileFieldKey, string>;
  for (const field of PROFILE_FIELDS) fields[field.key] = asText(raw[field.key]);
  return { id: asId(raw.id), fields };
};

export const fetchSelf = async (): Promise<SelfProfile> => {
  const { data } = await api.get<Envelope & { userList?: RawUser[] }>(`${IAM_BASE}/v1/users`);
  ensureSuccess(data);
  const user = data.userList?.[0];
  if (!user) throw new Error('USER_NOT_FOUND');
  return toSelfProfile(user);
};

/**
 * Profil guncelleme.
 *
 * SIFRE OPSIYONEL: bos birakilirsa "degistirme" demektir (sunucu da bos degeri
 * atlar). DOLUYSA sunucu kayit politikasinin aynisini uygular ve BASARIDA
 * KULLANICININ TUM OTURUMLARINI DUSURUR (deleteAllRefreshSessions +
 * revokeUserTokens). Cagri yerinin bunu bilmesi sart: kaydettikten sonra
 * ekranda kalmaya calisan bir arayuz, bir sonraki istekte 401 alip kullaniciyi
 * aciklamasiz giris ekranina firlatir.
 */
export const updateSelf = async (input: {
  fields: Record<ProfileFieldKey, string>;
  /** null = sifre degistirilmiyor. */
  userPassword: string | null;
}): Promise<void> => {
  const { data } = await api.post<Envelope>(`${IAM_BASE}/v1/users/update`, {
    ...input.fields,
    ...(input.userPassword === null ? {} : { userPassword: input.userPassword }),
  });
  ensureSuccess(data);
};

/**
 * HESAP SILME — geri alinamaz.
 *
 * Sunucu satiri silmez, ANONIMLESTIRIR ve tum oturumlari iptal eder. Mevzuat
 * geregi saklanmasi zorunlu islem kayitlari kimlik bilgilerinden ayrilarak
 * kalir (bkz. profile.deleteDescription). Bu yuzden cagri MUTLAKA bir onay
 * diyalogunun arkasindadir.
 */
export const deleteSelf = async (): Promise<void> => {
  // Govde BOS: sunucu hedefi cagiranin token'indan alir (ayricalikli roller
  // haric). Buraya bir id yazmak, sunucunun yok sayacagi bir alan olurdu.
  const { data } = await api.post<Envelope>(`${IAM_BASE}/v1/users/delete`, {});
  ensureSuccess(data);
};
